/* eslint-disable sonarjs/cognitive-complexity */
import { format, parseISO } from 'date-fns';
import { formatPhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input';

import { Error } from '@/helper/interfaces';
import { allCountries, allStatuses } from '@/helper/lists';

export function camelToTitle(input: string) {
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  const result = input.replace(/([A-Z]+)/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export function formatStatus(value: string, isFinished = false) {
  if (isFinished) {
    value = 'FINISHED';
  }
  return allStatuses.find((s) => s.key === value).value;
}

export function getColorClass(status: string) {
  let color = 'default';
  if (status == 'Firm') {
    color = 'yellow';
  } else if (status == 'Finished') {
    color = 'green';
  }
  return color;
}

export function formatCountry(value: string) {
  return allCountries.find((s) => s.key === value).value;
}

export function formatDate(date: string) {
  try {
    return format(parseISO(date), 'MMMM d, yyyy');
  } catch {
    return '';
  }
}

export function formatDateShort(date: string) {
  try {
    return format(parseISO(date), 'MM/dd/yy');
  } catch {
    return '';
  }
}

export function formatTime(date: string) {
  try {
    return format(parseISO(date), 'h:mm a');
  } catch {
    return '';
  }
}

export function formatMoney(value: number | null) {
  return typeof value === 'number' ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '$0.00';
}

export function formatPhone(value: string) {
  return value.startsWith('+1') ? formatPhoneNumber(value) : formatPhoneNumberIntl(value);
}

export function formatAddress(address: string, city: string, state: string, zip: string, country: string) {
  return (
    (address ? address + '\n' : '') +
    (city ? city + ', ' : '') +
    (state ? state + ' ' : '') +
    (zip ?? '') +
    (country ? '\n' + formatCountry(country) : '')
  );
}

export function formatErrors(errorResponse: Error[]) {
  let errors = {} as any;
  if (errorResponse) {
    for (const error of errorResponse) {
      errors = {
        ...errors,
        [error.path]: error.msg
      };
    }
  }
  return errors;
}

export function removeKeysWithPrefix<T extends Record<string, any>>(object: T, prefix: string): T {
  const newObject: Partial<T> = {};
  for (const key in object) {
    if (key.startsWith(prefix)) {
      continue; // Skip keys with the specified prefix
    }
    // eslint-disable-next-line security/detect-object-injection
    newObject[key] = object[key];
  }
  return newObject as T;
}

// for testing loading states add .then(sleeper(5000)) to api promise chain
export function sleeper(ms: number) {
  return function (x: any) {
    return new Promise((resolve) => setTimeout(() => resolve(x), ms));
  };
}

export function createPDF(data: Blob, id: number, name: string) {
  const blob = new Blob([data], { type: 'application/pdf' });
  downloadFile(blob, name + '.pdf');
}

export function createCSV(data: string, name: string) {
  const blob = new Blob([data], { type: 'text/csv' });
  downloadFile(blob, name + '.csv');
}

function downloadFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function mapSearchFormToArray(input: object) {
  // mapping based on which field exists
  const mapGroupToObject = (group: any) => {
    if (group.companyId) {
      return { type: 'company', id: Number(group.companyId), operator: group.condition || 'NA' };
    }
    if (group.contactId) {
      return { type: 'contact', id: Number(group.contactId), operator: group.condition || 'NA' };
    }
    if (group.projectId) {
      return { type: 'project', id: Number(group.projectId), operator: group.condition || 'NA' };
    }
    if (group.startDate != undefined && group.endDate != undefined) {
      return {
        type: 'dateRange',
        startDate: group.startDate,
        endDate: group.endDate,
        operator: group.condition || 'NA'
      };
    }
    if (group.mixerId) {
      return { type: 'mixer', id: Number(group.mixerId), operator: group.condition || 'NA' };
    }
    if (group.status != undefined) {
      // account for empty string
      return { type: 'status', name: group.status, operator: group.condition || 'NA' };
    }
    if (group.titles != undefined) {
      return { type: 'titles', name: group.titles, operator: group.condition || 'NA' };
    }
    if (group.voTalent != undefined) {
      return { type: 'voTalent', name: group.voTalent, operator: group.condition || 'NA' };
    }
  };

  // 1. Group fields by index prefix
  const grouped = {} as any;
  for (const [key, value] of Object.entries(input)) {
    // eslint-disable-next-line security/detect-object-injection
    const [index, field] = key.split('_');
    // eslint-disable-next-line security/detect-object-injection
    if (!grouped[index]) grouped[index] = {};
    // eslint-disable-next-line security/detect-object-injection
    grouped[index][field] = value;
  }

  // 2. Convert each group to target object
  return (
    Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      // eslint-disable-next-line security/detect-object-injection
      .map((key) => mapGroupToObject(grouped[key]))
      .filter(Boolean)
  );
}

export function omitKeysWithPrefix(object: object, prefixInt: number) {
  const prefix = `${prefixInt}_`;
  return Object.fromEntries(Object.entries(object).filter(([key]) => !key.startsWith(prefix)));
}

export function safeJSONParse(string: string) {
  try {
    return JSON.parse(string);
  } catch {
    return;
  }
}
